import { cn } from '../../lib/cn.js'
import { Text } from '../ui/Typography.js'
import { Skeleton } from '../ui/Skeleton.js'
import { ADMIN_ASSETS } from '../../lib/adminAssets.js'

export interface Bar {
  /** The label under the column — a weekday initial, a month. */
  label: string
  value: number
  /** What the tooltip says. The figure with its units, and anything beside it. */
  title?: string
}

export interface BarChartProps {
  bars: Bar[] | undefined
  height?: number
  loading?: boolean
  /** Drawn when every bar is nought, instead of a row of hairlines. */
  emptyText?: string
}

/**
 * Seven days as columns. 9.1's chart, and nothing more elaborate.
 *
 * **A quiet day is a hairline, not a gap.** The product says why, about the food
 * chart: *"A missing bar is a day nobody logged, not a day nobody ate."* The same
 * reading applies here — a Sunday with no bookings is a fact, and drawing nothing
 * makes it look like a day the chart failed to load.
 *
 * Written rather than installed. A charting library is 50KB and a great deal of
 * configuration for seven rectangles; when this needs axes and a legend, that is
 * the moment to reach for one.
 */
export function BarChart({ bars, height = 120, loading, emptyText }: BarChartProps) {
  if (loading || !bars) {
    return (
      <div className="flex items-end gap-2" style={{ height }}>
        {Array.from({ length: 7 }, (_, i) => (
          <Skeleton key={i} className="flex-1" style={{ height: `${30 + ((i * 37) % 60)}%` }} />
        ))}
      </div>
    )
  }

  const tallest = Math.max(...bars.map((b) => b.value))
  if (tallest === 0 && emptyText) {
    return (
      <div className="flex flex-col items-center justify-center gap-2" style={{ minHeight: height }}>
        <img
          src={ADMIN_ASSETS.reports}
          width={80}
          height={60}
          alt=""
          decoding="async"
          className="h-[60px] w-20 object-contain"
        />
        <Text variant="secondary">{emptyText}</Text>
      </div>
    )
  }

  return (
    <div className="flex items-end gap-2" style={{ height }}>
      {bars.map((b, i) => (
        <div key={`${b.label}-${i}`} className="flex flex-1 flex-col items-center gap-2">
          <div
            title={b.title ?? `${b.label}: ${b.value}`}
            className={cn('w-full rounded-sm bg-coral-fill transition-[height]')}
            style={{
              // At least 2px, always: a nought is a day nobody booked, and a gap
              // reads as a day the chart could not draw.
              height: `${Math.max(2, tallest === 0 ? 0 : (b.value / tallest) * (height - 24))}px`,
            }}
          />
          <Text as="span" variant="micro">
            {b.label}
          </Text>
        </div>
      ))}
    </div>
  )
}
